/**
 * Fehlende Prüfschritt-Überschriften im fertigen Bericht aus den Titeln des Prüfprompts füllen.
 *
 * Die Überschrift eines Prüfschritts kommt primär vom Prüfmodell selbst (`check_parameter`).
 * Das Feld ist aber nicht garantiert: das Modell lässt es gelegentlich leer oder liefert eine
 * inhaltslose Nummerierung, und zwar von Lauf zu Lauf unterschiedlich. Der `AiReportAdapter`
 * kann dann nur von einer anderen Antwort DERSELBEN Regel abschreiben – gibt es dort ebenfalls
 * keinen Titel, bleibt die Überschrift leer.
 *
 * Diese Stufe schließt genau diese Lücke aus einer Quelle, die der Adapter nicht kennt: den im
 * Regelset gespeicherten Prüfschritt-Titeln (aus dem Prüfprompt, siehe `pruefprompt-steps.ts`).
 * Sie sind set-weit, stabil und kosten zur Anzeige nichts.
 *
 * Rein, ohne Angular.
 */

import {NormalizedReport, ReportDetail, ReportRule} from './normalized-report.model';

/**
 * Prüfschritt-Index einer Detailzeile INNERHALB ihrer Position.
 *
 * Im Positions-Modus (Prüfebene ≠ Gesamtdokument) hängen alle Positionen einer Regel in einer
 * flachen Detailliste (Position 1 Schritt 1..n, Position 2 Schritt 1..n, …). Der Listenindex ist
 * dort also KEIN Schrittindex. Die Position steht im Rohergebnis der Antwort (`rawOutput`).
 */
function positionOf(detail: ReportDetail): string {
  const raw: any = detail.rawOutput;
  const position = raw?.position;
  return typeof position === 'string' ? position.trim() : '';
}

function stepIndices(details: ReadonlyArray<ReportDetail>): number[] {
  const stepsPerPosition = new Map<string, number>();
  return details.map(detail => {
    const position = positionOf(detail);
    const step = stepsPerPosition.get(position) ?? 0;
    stepsPerPosition.set(position, step + 1);
    return step;
  });
}

function fillRule(rule: ReportRule, titles: ReadonlyArray<string>): boolean {
  const details = rule.details ?? [];
  const indices = stepIndices(details);
  let changed = false;
  details.forEach((detail, index) => {
    if (detail.heading) return;
    const title = (titles[indices[index]] ?? '').trim();
    if (!title) return;
    detail.heading = title;
    changed = true;
  });
  return changed;
}

/**
 * Trägt die Prüfschritt-Titel in alle Detailzeilen ohne Überschrift ein – in der Datei- UND in
 * der Strukturebene-Sicht. Vorhandene Überschriften bleiben unangetastet: der Titel des Modells
 * ist spezifischer als der generische Schritt-Titel des Prompts.
 *
 * Mutiert den Bericht in place (die beiden Sichten teilen sich dieselben Detail-Objekte) und
 * meldet über den Rückgabewert, ob sich etwas geändert hat – nur dann muss die Ansicht ihre
 * Caches verwerfen.
 */
export function fillMissingStepHeadings(
  report: NormalizedReport | null | undefined,
  titles: ReadonlyArray<string> | null | undefined,
): boolean {
  if (!report || !titles || titles.length === 0) return false;
  let changed = false;
  for (const group of report.groups ?? []) {
    for (const rule of group.rules ?? []) {
      if (fillRule(rule, titles)) changed = true;
    }
    for (const structure of group.structures ?? []) {
      for (const rule of structure.rules ?? []) {
        if (fillRule(rule, titles)) changed = true;
      }
    }
  }
  return changed;
}
